import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SITE_URL } from '../screens/legal/shared';

// Mirrors landing/templates.mjs nav(): brand -> zmist.co, the same five
// destinations in the same order, and the primary "Create a channel"
// button. Explore stays in-app (/public); the marketing pages live on
// zmist.co. Below `sm` the links collapse into a menu so every one stays
// reachable.
export const PUBLIC_NAV_LINKS: { label: string; href: string; external: boolean }[] = [
  { label: 'Explore', href: '/public', external: false },
  { label: 'How it works', href: `${SITE_URL}/how-it-works/`, external: true },
  { label: 'Pricing', href: `${SITE_URL}/pricing/`, external: true },
  { label: 'About', href: `${SITE_URL}/about/`, external: true },
];

const LINK_CLASS = 'text-[14px] font-medium text-(--color-text)/75 hover:text-(--color-accent) no-underline';

function NavLink({ link, onClick }: { link: (typeof PUBLIC_NAV_LINKS)[number]; onClick?: () => void }) {
  return link.external ? (
    <a href={link.href} className={LINK_CLASS} onClick={onClick}>
      {link.label}
    </a>
  ) : (
    <Link to={link.href} className={LINK_CLASS} onClick={onClick}>
      {link.label}
    </Link>
  );
}

export function PublicSiteHeader() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <header className="w-full border-b border-(--color-divider) bg-(--color-bg)/90 text-(--color-text) backdrop-blur-sm sticky top-0 z-20 shrink-0">
      <div className="w-full px-[clamp(20px,5vw,72px)] h-16 flex items-center justify-between gap-4">
        <a href={SITE_URL} className="flex items-center gap-2 shrink-0 no-underline text-inherit">
          <img src="/zmist-logo.svg" alt="" className="w-7 h-7" />
          <span className="text-[18px] font-extrabold tracking-tight">Zmist</span>
        </a>
        <nav aria-label="Main" className="hidden sm:flex items-center gap-6">
          {PUBLIC_NAV_LINKS.map((l) => (
            <NavLink key={l.label} link={l} />
          ))}
          <Link to="/login" className={LINK_CLASS}>
            Log in
          </Link>
          <Link
            to="/register"
            className="inline-flex items-center justify-center rounded-xl px-4 py-2 text-[14px] font-semibold text-(--color-text) dark:text-(--color-bg) bg-(--color-accent) hover:brightness-95 transition no-underline"
          >
            Create a channel
          </Link>
        </nav>
        <button
          type="button"
          className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-(--color-divider)"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="public-mobile-menu"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <nav
          id="public-mobile-menu"
          aria-label="Mobile"
          className="sm:hidden flex flex-col gap-4 px-4 pb-5 pt-2 border-t border-(--color-divider)"
        >
          {PUBLIC_NAV_LINKS.map((l) => (
            <NavLink key={l.label} link={l} onClick={close} />
          ))}
          <Link to="/login" className={LINK_CLASS} onClick={close}>
            Log in
          </Link>
          <Link
            to="/register"
            onClick={close}
            className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-[14px] font-semibold text-(--color-text) dark:text-(--color-bg) bg-(--color-accent) no-underline"
          >
            Create a channel
          </Link>
        </nav>
      )}
    </header>
  );
}
